import React, {useMemo, useState} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import {t} from 'i18next';
import './QueryHeader.scss';
import {SessionSearch} from '../../types/SessionSearch';
import ArrButton from '../../../common/arr-button/ArrButton';
import {hasAnyCriteriaAdded} from '../../utilities/utilities';
import {
  pinnedRows,
  rows,
} from '../../../ingetion-analytics/index-management/IndexManagement';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../shared/types';

export interface QueryHeaderProps {
  item: SessionSearch;
  isSearchMode?: boolean;
  onCopy?: (item: SessionSearch) => void;
  onSave?: (item: SessionSearch) => void;
  onSearch?: (item: SessionSearch) => void;
  onModify?: (item: SessionSearch) => void;
}

const QueryHeader: React.FC<QueryHeaderProps> = ({
  item,
  onCopy,
  onSave,
  onSearch,
  onModify,
}) => {
  const hasSearchBeenExecuted = !!(item.lastRuntime || item.advancedSearchId);

  const handleCopyButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onCopy?.(item);
  };

  const handleCopyButtonKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      onCopy?.(item);
    }
  };

  const handleSaveButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onSave?.(item);
  };

  const handleSaveButtonKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      onSave?.(item);
    }
  };

  const handleSearchButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onSearch?.(item);
  };

  const handleSearchButtonKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      onSearch?.(item);
    }
  };

  const handleModifyButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onModify?.(item);
  };

  const handleModifyButtonKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      onModify?.(item);
    }
  };

  const searchIndexData = [...pinnedRows.top, ...rows];
  const completeIndexData = searchIndexData.filter(
    (item) => (item?.status ?? '').toLowerCase() === 'complete'
  );

  const dropdownItems: ISelectItem[] = useMemo(
    () =>
      completeIndexData.map((d) => ({
        id: d.id,
        value: d.indexName,
        label: d.indexName,
      })),
    []
  );
  const [selectedDataset, setSelectedDataset] = useState<ISelectItem | null>(
    dropdownItems[0]
  );

  const handleDatasetChange = (item: ISelectItem | null) => {
    setSelectedDataset(item);
  };

  const renderSearchButton = () => {
    let component = <></>;

    if (!hasSearchBeenExecuted) {
      component = (
        <ArrButton
          startIcon={<SearchIcon />}
          size="small"
          onClick={handleSearchButtonClick}
          onKeyDown={handleSearchButtonKeyDown}
          isAriaDisabled={!hasAnyCriteriaAdded(item)}
          className={`button-search ${hasAnyCriteriaAdded(item) ? '' : 'btn-disabled'}`}
          dataTestId="button-search"
        >
          {t('sessionSearch.queryHeader.searchButton')}
        </ArrButton>
      );
    }

    return component;
  };

  const renderModifyButton = () => {
    let component = <></>;

    if (hasSearchBeenExecuted) {
      component = (
        <ArrButton
          size="small"
          onClick={handleModifyButtonClick}
          onKeyDown={handleModifyButtonKeyDown}
          className="button-modify"
          dataTestId="button-modify"
        >
          {t('sessionSearch.queryHeader.modifyButton')}
        </ArrButton>
      );
    }

    return component;
  };

  return (
    <Box className="session-query-header-wrapper">
      <Box className="session-query-header-title">
        <Typography
          className="typo-text subhead3_size query-header-text"
          title={item.name}
          data-testid={`query-header-name-${item.id}`}
          tabIndex={0}
        >
          {item.name}
        </Typography>
      </Box>
      <Stack
        direction={'row'}
        spacing={1}
        className="session-search-query-header"
      >
        {/* This code needs to uncommit for future release 1.1 */}
       
        {/* <ArrSingleSelectDropdown
          sizeVariant="small"
          className="dropdown-select-index "
          dropdownLabel={t('sessionSearch.queryHeader.indexDropdown')}
          selectedValues={selectedDataset}
          dropdownItems={dropdownItems}
          onSelectionChange={handleDatasetChange}
          dropdownItemKey="id"
          dropdownItemDisplay="label"
          searchable={true}
          aria-label={t('sessionSearch.queryHeader.indexDropdown')}
          dataTestId="asd-query-index-dropdown"
        /> */}
      
        <Box>
          <ArrButton
            startIcon={<ContentCopyIcon />}
            size="small"
            variant="text"
            onClick={handleCopyButtonClick}
            onKeyDown={handleCopyButtonKeyDown}
            isAriaDisabled={!hasAnyCriteriaAdded(item)}
            className={`button-copy ${hasAnyCriteriaAdded(item) ? '' : 'btn-disabled'}`}
            dataTestId="button-copy"
          >
            {t('sessionSearch.queryHeader.copyButton')}
          </ArrButton>

          <ArrButton
            startIcon={<SaveIcon />}
            size="small"
            onClick={handleSaveButtonClick}
            onKeyDown={handleSaveButtonKeyDown}
            isAriaDisabled={!hasAnyCriteriaAdded(item)}
            className={`button-save ${hasAnyCriteriaAdded(item) ? '' : 'btn-disabled'}`}
            dataTestId="button-save"
          >
            {t('sessionSearch.queryHeader.saveButton')}
          </ArrButton>

          {renderSearchButton()}

          {renderModifyButton()}
        </Box>
      </Stack>
    </Box>
  );
};

export default QueryHeader;
